import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useManualCategories, useManuals } from '../api/queries'
import type { Manual } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDate } from '../lib/format'

/**
 * Manualbiblioteket (docs/DECISIONS.md beslutning 063).
 *
 * Husets egne arbejdsgange og leverandørernes færdige PDF'er står samme sted:
 * den, der leder efter "hvordan idriftsætter vi en Easee", er ligeglad med, om
 * svaret er skrevet af en kollega eller kom med i kassen - han skal bare finde
 * det.
 *
 * <b>Søgefeltet står øverst og har fokus fra start.</b> Et bibliotek bliver
 * hurtigt for stort til at bladre i, og den, der åbner siden, ved som regel
 * allerede, hvad han leder efter.
 */
export function ManualsPage() {
  const { isProjectManager } = useAuth()

  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)

  const { data: categories } = useManualCategories()
  const { data, isLoading, error } = useManuals({
    search: search.trim() || undefined,
    categoryId,
    includeInactive
  })

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Manualer
        </h1>
        {isProjectManager && (
          <Link className="btn btn--primary" to="/manuals/new">
            Ny manual
          </Link>
        )}
      </div>

      <Card>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            autoFocus
            style={{ minWidth: 220, flex: 1 }}
            placeholder="Søg i titel, beskrivelse og filnavn"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <select
            style={{ maxWidth: 220 }}
            value={categoryId ?? ''}
            onChange={(event) => setCategoryId(event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">Alle hylder</option>
            {(categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name} ({category.manualCount})
              </option>
            ))}
          </select>

          <label className="small row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Vis udgåede
          </label>
        </div>
      </Card>

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || categoryId
            ? 'Ingen manualer passer på det, du søgte efter.'
            : 'Der er ingen manualer endnu. Skriv den første, eller læg en leverandørmanual op.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 2 }}>
            {data.map((manual) => (
              <ManualRow key={manual.id} manual={manual} />
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

function ManualRow({ manual }: { manual: Manual }) {
  return (
    <Link
      className="row row--between device-row"
      to={`/manuals/${manual.id}`}
      style={{ gap: 12, alignItems: 'flex-start', opacity: manual.isActive ? 1 : 0.55 }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600 }}>
          {manual.kind === 'file' ? '📄' : '📝'} {manual.title}
          {!manual.isActive && <span className="tiny muted"> · udgået</span>}
        </div>

        {manual.summary && <div className="small muted">{manual.summary}</div>}

        {/* Hvem der har lavet den, er en del af selve manualen (beslutning 063)
            og staar derfor paa listen - ikke gemt inde paa detaljesiden. */}
        <div className="tiny muted">
          {manual.createdBy?.displayName ?? 'Ukendt'}
          {manual.kind === 'file' ? ' lagde den op ' : ' skrev den '}
          {formatDate(manual.createdAt)}
          {manual.updatedBy && manual.updatedBy.id !== manual.createdBy?.id && (
            <> · sidst rettet af {manual.updatedBy.displayName} {formatDate(manual.updatedAt)}</>
          )}
        </div>

        {(manual.deviceTypes.length > 0 || manual.templates.length > 0) && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {manual.deviceTypes.map((type) => (
              <span key={type} className="pill" style={{ background: '#E0F2FE', color: '#075985' }}>
                {type}
              </span>
            ))}
            {manual.templates.map((template) => (
              <span key={template.id} className="pill" style={{ background: '#EDE9FE', color: '#5B21B6' }}>
                {template.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="tiny muted" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {manual.categoryName ?? 'Uden hylde'}
        <div>{manual.kind === 'file' ? describeSize(manual.sizeBytes) : manual.kindName}</div>
      </div>
    </Link>
  )
}

/** Filstørrelsen i noget, man kan forholde sig til på en telefon med dårlig dækning. */
export function describeSize(bytes?: number | null) {
  if (bytes === null || bytes === undefined) return 'Ingen fil endnu'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`

  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}
